/**
 * Email API - Connects to Django backend which proxies to NestJS email service
 *
 * The Django backend at /api/email-campaigns/ acts as a gateway to the NestJS
 * email service. This provides:
 * - Single authentication flow (Django JWT)
 * - Credit/token management integration
 * - Consistent API patterns across the app
 */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { tokenStorage } from '@utils/tokenStorage';
import { USER_ACCESS_TOKEN } from '@constants/auth';
import { withReauth } from './baseQueryWithReauth';

// Email API types
// Email Project types
export interface EmailProject {
  id: string;
  name: string;
  description?: string;
  color?: string; // Hex color for UI badges (e.g., "#3B82F6")
  clientId: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    emailAccounts: number;
  };
}

export interface CreateEmailProjectDto {
  name: string;
  description?: string;
  color?: string;
}

export interface UpdateEmailProjectDto {
  name?: string;
  description?: string;
  color?: string;
}

export interface WarmupStats {
  date: string;
  emailsSent: number;
  emailsOpened: number;
  emailsReplied: number;
  spamCount: number;
  inboxCount: number;
}

export interface WarmupProfile {
  id: string;
  enabled: boolean;
  completed: boolean;
  warmupDurationDays: number;
  totalWarmupPerDay: number;
  dailyRampUp: number;
  replyRatePercentage: number;
  warmupReputation?: string;
  maxThreadDepth: number;
  continueReplyRate: number;
  maintenanceEnabled: boolean;
  maintenanceDailyEmails: number;
  createdAt: string;
  updatedAt: string;
  warmupStats?: WarmupStats[];
}

export interface EmailAccount {
  id: string;
  email: string;
  fromName?: string;
  providerType: 'OAUTH' | 'IMAP_SMTP';
  status: 'ACTIVE' | 'FAILED' | 'DISABLED' | 'WARMING_UP';
  maxEmailPerDay?: number;
  warmupEnabled?: boolean;
  warmupProfile?: WarmupProfile;
  createdAt: string;
  updatedAt: string;
  // Project assignment
  projectId?: string;
  project?: {
    id: string;
    name: string;
    color?: string;
  };
}

export interface EmailCampaign {
  id: string;
  name: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED';
  sequences: EmailSequence[];
  leads: Lead[];
  leadsCount: number;
  emailAccounts: string[];
  schedule?: CampaignSchedule;
  statistics?: CampaignStatistics;
  createdAt: string;
  updatedAt: string;
}

/** Map Django snake_case campaign response to frontend camelCase */
function transformDjangoCampaign(raw: any): EmailCampaign {
  const statusMap: Record<string, EmailCampaign['status']> = {
    draft: 'DRAFT',
    running: 'ACTIVE',
    active: 'ACTIVE',
    paused: 'PAUSED',
    completed: 'COMPLETED',
    cancelled: 'COMPLETED',
  };

  const rawStats = raw.stats;
  const statistics: CampaignStatistics | undefined = rawStats
    ? {
        sent: rawStats.emails_sent ?? rawStats.sent ?? 0,
        opened: rawStats.emails_opened ?? rawStats.opened ?? 0,
        clicked: rawStats.emails_clicked ?? rawStats.clicked ?? 0,
        replied: rawStats.emails_replied ?? rawStats.replied ?? 0,
        bounced: rawStats.emails_bounced ?? rawStats.bounced ?? 0,
        unsubscribed: rawStats.emails_unsubscribed ?? rawStats.unsubscribed ?? 0,
      }
    : undefined;

  return {
    id: raw.id,
    name: raw.name,
    status: statusMap[(raw.status || '').toLowerCase()] || raw.status?.toUpperCase() || 'DRAFT',
    sequences: (raw.email_sequence || raw.sequences || []).map((s: any, i: number) => ({
      id: s.id || `seq-${i}`,
      name: s.name || `Step ${s.step_number || i + 1}`,
      subject: s.subject || '',
      body: s.body || '',
      order: s.step_number ?? s.order ?? i,
      delayDays: s.delay_days ?? s.delayDays ?? 0,
    })),
    leads: raw.leads || [],
    leadsCount: raw.leads_count ?? raw.leadsCount ?? raw.leads?.length ?? 0,
    emailAccounts: raw.email_accounts || raw.emailAccounts || [],
    schedule: raw.schedule_config || raw.schedule,
    statistics,
    createdAt: raw.created_at || raw.createdAt || '',
    updatedAt: raw.updated_at || raw.updatedAt || '',
  };
}

export interface EmailSequence {
  id: string;
  name: string;
  subject: string;
  body: string;
  order: number;
  delayDays: number;
  variants?: EmailVariant[];
}

export interface EmailVariant {
  id: string;
  name: string;
  subject: string;
  body: string;
  percentage: number;
}

export interface Lead {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  source?: string;
  sourceUrl?: string;
  customFields?: Record<string, any>;
  isBlocked: boolean;
  isUnsubscribed: boolean;
  status?: 'NEW' | 'CONTACTED' | 'REPLIED' | 'INTERESTED' | 'MEETING' | 'WON' | 'LOST';
  statusChangedAt?: string;
}

export interface CampaignSchedule {
  timezone: string;
  sendingDays: string[];
  startTime: string;
  endTime: string;
  dailyLimit?: number;
  hourlyLimit?: number;
}

export interface CampaignStatistics {
  sent: number;
  opened: number;
  clicked: number;
  replied: number;
  bounced: number;
  unsubscribed: number;
}

export interface TimeSeriesDataPoint {
  date: string;
  emailsSent: number;
  emailsOpened: number;
  emailsClicked: number;
  emailsReplied: number;
  emailsBounced: number;
  emailsUnsubscribed: number;
}

export type AnalyticsTimeSeriesResponse = TimeSeriesDataPoint[];

export type LeadStatusType = 'NEW' | 'CONTACTED' | 'REPLIED' | 'INTERESTED' | 'MEETING' | 'WON' | 'LOST';

export interface PipelineSummary {
  NEW: number;
  CONTACTED: number;
  REPLIED: number;
  INTERESTED: number;
  MEETING: number;
  WON: number;
  LOST: number;
}

export interface EmailThread {
  id: string;
  subject: string;
  from: string;
  to: string;
  preview: string;
  date: string;
  unread: boolean;
  campaignId?: string;
  campaignName?: string;
  messages: EmailMessage[];
}

export interface EmailMessage {
  id: string;
  from: string;
  to: string;
  subject: string;
  body: string;
  date: string;
  isReply: boolean;
  attachments?: string[];
}

export interface CreateEmailCampaignDto {
  name: string;
  sequences: Omit<EmailSequence, 'id'>[];
  emailAccountIds: string[];
  schedule?: CampaignSchedule;
  leadIds?: string[];
}

export interface ImportLeadsDto {
  file: File;
  mapping: Record<string, string>;
  campaignId?: string;
}

export interface SendReplyDto {
  threadId: string;
  body: string;
  attachments?: File[];
}

// Base query — routes through the Django → email-service proxy. Shares the app-wide
// auth/refresh/error handling via withReauth (previously a weaker bespoke copy that
// lacked logout-on-failure, the tier-limit modal, and 500/503 toasts).
const emailBaseQuery = fetchBaseQuery({
  baseUrl: '/api/email-campaigns',
  prepareHeaders: headers => {
    const token = tokenStorage.getToken(USER_ACCESS_TOKEN);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// Create the email API slice - routes through Django backend
export const emailApi = createApi({
  reducerPath: 'emailApi',
  baseQuery: withReauth(emailBaseQuery),
  tagTypes: ['EmailCampaign', 'EmailAccount', 'EmailTemplate', 'EmailThread', 'Lead', 'EmailProject'],
  endpoints: builder => ({
    // =========================================================================
    // Email Campaigns (Django: /api/email-campaigns/campaigns/)
    // =========================================================================
    getEmailCampaigns: builder.query<EmailCampaign[], void>({
      query: () => '/campaigns/',
      transformResponse: (response: any[]) => (Array.isArray(response) ? response.map(transformDjangoCampaign) : []),
      providesTags: ['EmailCampaign'],
    }),

    getEmailCampaign: builder.query<EmailCampaign, string>({
      query: id => `/campaigns/${id}/`,
      transformResponse: (response: any) => transformDjangoCampaign(response),
      providesTags: ['EmailCampaign'],
    }),

    createEmailCampaign: builder.mutation<EmailCampaign, CreateEmailCampaignDto>({
      query: data => ({
        url: '/campaigns/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailCampaign'],
    }),

    updateEmailCampaign: builder.mutation<EmailCampaign, { id: string; data: Partial<EmailCampaign> }>({
      query: ({ id, data }) => ({
        url: `/campaigns/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['EmailCampaign'],
    }),

    deleteEmailCampaign: builder.mutation<void, string>({
      query: id => ({
        url: `/campaigns/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailCampaign'],
    }),

    startEmailCampaign: builder.mutation<{ message: string; campaign_id: string }, string>({
      query: id => ({
        url: `/campaigns/${id}/start/`,
        method: 'POST',
      }),
      invalidatesTags: ['EmailCampaign'],
    }),

    pauseEmailCampaign: builder.mutation<{ message: string; campaign_id: string }, string>({
      query: id => ({
        url: `/campaigns/${id}/pause/`,
        method: 'POST',
      }),
      invalidatesTags: ['EmailCampaign'],
    }),

    resumeEmailCampaign: builder.mutation<{ message: string; campaign_id: string }, string>({
      query: id => ({
        url: `/campaigns/${id}/resume/`,
        method: 'POST',
      }),
      invalidatesTags: ['EmailCampaign'],
    }),

    cancelEmailCampaign: builder.mutation<{ message: string; campaign_id: string }, string>({
      query: id => ({
        url: `/campaigns/${id}/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: ['EmailCampaign'],
    }),

    getCampaignStats: builder.query<CampaignStatistics, string>({
      query: id => `/campaigns/${id}/stats/`,
    }),

    getCampaignLeads: builder.query<any, { campaignId: string; status?: string; page?: number; page_size?: number }>({
      query: ({ campaignId, status, page, page_size }) => ({
        url: `/campaigns/${campaignId}/leads/`,
        params: {
          ...(status ? { status } : {}),
          ...(page ? { page } : {}),
          ...(page_size ? { page_size } : { page_size: 25 }),
        },
      }),
      providesTags: ['Lead'],
    }),

    addLeadsToCampaign: builder.mutation<
      { message: string; total_leads: number },
      { campaignId: string; lead_ids?: string[]; csv_data?: string }
    >({
      query: ({ campaignId, ...body }) => ({
        url: `/campaigns/${campaignId}/leads/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EmailCampaign', 'Lead'],
    }),

    cloneEmailCampaign: builder.mutation<EmailCampaign, { id: string; include_leads?: boolean }>({
      query: ({ id, include_leads }) => ({
        url: `/campaigns/${id}/clone/`,
        method: 'POST',
        body: { include_leads },
      }),
      invalidatesTags: ['EmailCampaign'],
    }),

    // =========================================================================
    // Email Accounts (Django: /api/email-campaigns/accounts/)
    // =========================================================================
    getEmailAccounts: builder.query<EmailAccount[], { projectId?: string; unassigned?: boolean } | void>({
      query: params => ({
        url: '/accounts/',
        params: params || undefined,
      }),
      providesTags: ['EmailAccount'],
    }),

    getEmailAccount: builder.query<EmailAccount, string>({
      query: id => `/accounts/${id}/`,
      providesTags: ['EmailAccount'],
    }),

    addEmailAccount: builder.mutation<EmailAccount, Partial<EmailAccount>>({
      query: data => ({
        url: '/accounts/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailAccount'],
    }),

    updateEmailAccount: builder.mutation<EmailAccount, { id: string; data: Partial<EmailAccount> }>({
      query: ({ id, data }) => ({
        url: `/accounts/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['EmailAccount'],
    }),

    deleteEmailAccount: builder.mutation<void, string>({
      query: id => ({
        url: `/accounts/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailAccount'],
    }),

    testEmailAccount: builder.mutation<{ message: string; details: any }, string>({
      query: id => ({
        url: `/accounts/${id}/test/`,
        method: 'POST',
      }),
    }),

    // OAuth flows
    initGmailOAuth: builder.mutation<{ authUrl: string; state: string }, void>({
      query: () => ({
        url: '/accounts/oauth/gmail/init/',
        method: 'POST',
      }),
    }),

    completeGmailOAuth: builder.mutation<EmailAccount, { code: string; state: string }>({
      query: data => ({
        url: '/accounts/oauth/gmail/callback/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailAccount'],
    }),

    initOutlookOAuth: builder.mutation<{ authUrl: string; state: string }, void>({
      query: () => ({
        url: '/accounts/oauth/outlook/init/',
        method: 'POST',
      }),
    }),

    completeOutlookOAuth: builder.mutation<EmailAccount, { code: string; state: string }>({
      query: data => ({
        url: '/accounts/oauth/outlook/callback/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailAccount'],
    }),

    // IMAP/SMTP account creation
    createImapSmtpAccount: builder.mutation<
      EmailAccount,
      {
        email: string;
        fromName: string;
        username: string;
        maxEmailPerDay?: number;
        provider: {
          password: string;
          smtpHost: string;
          smtpPort: number;
          smtpUseTls: boolean;
          imapHost: string;
          imapPort: number;
          imapUseTsl: boolean;
        };
      }
    >({
      query: data => ({
        url: '/accounts/imap-smtp/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailAccount'],
    }),

    // Warmup
    getWarmupStatus: builder.query<any, string>({
      query: id => `/accounts/${id}/warmup_status/`,
    }),

    startWarmup: builder.mutation<{ message: string }, { id: string; config?: any }>({
      query: ({ id, config }) => ({
        url: `/accounts/${id}/start_warmup/`,
        method: 'POST',
        body: { config },
      }),
      invalidatesTags: ['EmailAccount'],
    }),

    pauseWarmup: builder.mutation<{ message: string }, string>({
      query: id => ({
        url: `/accounts/${id}/pause_warmup/`,
        method: 'POST',
      }),
      invalidatesTags: ['EmailAccount'],
    }),

    updateWarmupMaintenance: builder.mutation<
      { message: string },
      { id: string; maintenanceEnabled: boolean; maintenanceDailyEmails?: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/accounts/${id}/warmup_maintenance/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['EmailAccount'],
    }),

    // Assign account to project
    assignAccountToProject: builder.mutation<EmailAccount, { accountId: string; projectId: string | null }>({
      query: ({ accountId, projectId }) => ({
        url: `/accounts/${accountId}/project/`,
        method: 'PATCH',
        body: { projectId },
      }),
      invalidatesTags: ['EmailAccount', 'EmailProject'],
    }),

    // =========================================================================
    // Email Projects (Django: /api/email-campaigns/projects/)
    // =========================================================================
    getEmailProjects: builder.query<EmailProject[], void>({
      query: () => '/projects/',
      providesTags: ['EmailProject'],
    }),

    getEmailProject: builder.query<EmailProject & { emailAccounts: EmailAccount[] }, string>({
      query: id => `/projects/${id}/`,
      providesTags: ['EmailProject'],
    }),

    createEmailProject: builder.mutation<EmailProject, CreateEmailProjectDto>({
      query: data => ({
        url: '/projects/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailProject'],
    }),

    updateEmailProject: builder.mutation<EmailProject, { id: string; data: UpdateEmailProjectDto }>({
      query: ({ id, data }) => ({
        url: `/projects/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['EmailProject'],
    }),

    deleteEmailProject: builder.mutation<void, string>({
      query: id => ({
        url: `/projects/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailProject', 'EmailAccount'],
    }),

    // Bulk assign accounts to project
    bulkAssignAccountsToProject: builder.mutation<
      { message: string; count: number },
      { projectId: string; accountIds: string[] }
    >({
      query: ({ projectId, accountIds }) => ({
        url: `/projects/${projectId}/accounts/`,
        method: 'PATCH',
        body: { accountIds },
      }),
      invalidatesTags: ['EmailAccount', 'EmailProject'],
    }),

    // Remove accounts from project
    removeAccountsFromProject: builder.mutation<
      { message: string; count: number },
      { projectId: string; accountIds: string[] }
    >({
      query: ({ projectId, accountIds }) => ({
        url: `/projects/${projectId}/accounts/`,
        method: 'DELETE',
        body: { accountIds },
      }),
      invalidatesTags: ['EmailAccount', 'EmailProject'],
    }),

    // =========================================================================
    // Email Templates (Django: /api/email-campaigns/templates/)
    // =========================================================================
    getEmailTemplates: builder.query<EmailTemplate[], void>({
      query: () => '/templates/',
      providesTags: ['EmailTemplate'],
    }),

    getEmailTemplate: builder.query<EmailTemplate, string>({
      query: id => `/templates/${id}/`,
      providesTags: ['EmailTemplate'],
    }),

    createEmailTemplate: builder.mutation<EmailTemplate, Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'>>({
      query: data => ({
        url: '/templates/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailTemplate'],
    }),

    updateEmailTemplate: builder.mutation<EmailTemplate, { id: string; data: Partial<EmailTemplate> }>({
      query: ({ id, data }) => ({
        url: `/templates/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['EmailTemplate'],
    }),

    deleteEmailTemplate: builder.mutation<void, string>({
      query: id => ({
        url: `/templates/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailTemplate'],
    }),

    duplicateEmailTemplate: builder.mutation<EmailTemplate, string>({
      query: id => ({
        url: `/templates/${id}/duplicate/`,
        method: 'POST',
      }),
      invalidatesTags: ['EmailTemplate'],
    }),

    // =========================================================================
    // Master Inbox (Django: /api/email-campaigns/master-inbox/)
    // =========================================================================
    getEmailThreads: builder.query<
      { threads: EmailThread[]; total_count: number; unread_count: number },
      { unread?: boolean; campaignId?: string; page?: number }
    >({
      query: params => ({
        url: '/master-inbox/threads/',
        params,
      }),
      transformResponse: (response: any): { threads: EmailThread[]; total_count: number; unread_count: number } => {
        // If response already has flat threads array, use it directly
        if (response?.threads && Array.isArray(response.threads)) {
          return response;
        }
        // NestJS returns { accountThreads: [{ accountId, accountEmail, threads: [...] }] }
        // Flatten into a single threads array
        const allThreads: EmailThread[] = [];
        if (response?.accountThreads && Array.isArray(response.accountThreads)) {
          for (const account of response.accountThreads) {
            if (account.threads && Array.isArray(account.threads)) {
              for (const thread of account.threads) {
                const latest = thread.latestEmailInThread;
                if (!latest || thread.isWarmup) continue;
                allThreads.push({
                  id: thread.initialMessageId,
                  subject: latest.subject || '',
                  from: Array.isArray(latest.from) ? latest.from[0] : latest.from || '',
                  to: Array.isArray(latest.to) ? latest.to[0] : latest.to || '',
                  preview: latest.body?.replace(/<[^>]*>/g, '').slice(0, 150) || '',
                  date: latest.time || '',
                  unread: false,
                  campaignId: undefined,
                  campaignName: undefined,
                  messages: [
                    {
                      id: thread.initialMessageId,
                      from: Array.isArray(latest.from) ? latest.from[0] : latest.from || '',
                      to: Array.isArray(latest.to) ? latest.to[0] : latest.to || '',
                      subject: latest.subject || '',
                      body: latest.body || '',
                      date: latest.time || '',
                      isReply: latest.type === 'RECEIVED' || latest.type === 'REPLY',
                    },
                  ],
                });
              }
            }
          }
        }
        // Sort by date descending
        allThreads.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        return {
          threads: allThreads,
          total_count: allThreads.length,
          unread_count: 0,
        };
      },
      providesTags: ['EmailThread'],
    }),

    getEmailThread: builder.query<EmailThread, string>({
      query: id => `/master-inbox/${id}/thread/`,
      providesTags: ['EmailThread'],
    }),

    sendReply: builder.mutation<{ message: string; thread_id: string }, SendReplyDto>({
      query: data => ({
        url: '/master-inbox/reply/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailThread'],
    }),

    markAsRead: builder.mutation<{ message: string }, string[]>({
      query: threadIds => ({
        url: '/master-inbox/mark_read/',
        method: 'POST',
        body: { thread_ids: threadIds },
      }),
      invalidatesTags: ['EmailThread'],
    }),

    // =========================================================================
    // Analytics (Django: /api/email-campaigns/email-analytics/)
    // =========================================================================
    getAnalyticsOverview: builder.query<any, { start_date?: string; end_date?: string; include_warmup?: boolean }>({
      query: params => ({
        url: '/email-analytics/overview/',
        params,
      }),
    }),

    getCampaignAnalytics: builder.query<any, string>({
      query: campaignId => `/email-analytics/${campaignId}/campaign/`,
    }),

    getAnalyticsTimeSeries: builder.query<AnalyticsTimeSeriesResponse, { start_date?: string; end_date?: string }>({
      query: params => ({
        url: '/email-analytics/timeseries/',
        params,
      }),
      transformResponse: (response: any): AnalyticsTimeSeriesResponse => {
        // NestJS returns { performance: [{ date, sent, opened, ... }], totalDays, includeWarmup }
        // Transform to the flat array format the frontend expects
        const dataPoints = Array.isArray(response) ? response : response?.performance || [];
        return dataPoints.map((point: any) => ({
          date: point.date,
          emailsSent: point.sent ?? point.emailsSent ?? 0,
          emailsOpened: point.opened ?? point.emailsOpened ?? 0,
          emailsClicked: point.clicked ?? point.emailsClicked ?? 0,
          emailsReplied: point.replied ?? point.emailsReplied ?? 0,
          emailsBounced: point.bounced ?? point.emailsBounced ?? 0,
          emailsUnsubscribed: point.unsubscribed ?? point.emailsUnsubscribed ?? 0,
        }));
      },
    }),

    // =========================================================================
    // Lead Pipeline (Django proxy to NestJS)
    // =========================================================================
    updateLeadStatus: builder.mutation<Lead, { leadId: string; status: string }>({
      query: ({ leadId, status }) => ({
        url: `/leads/${leadId}/status/`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['Lead'],
    }),

    bulkUpdateLeadStatus: builder.mutation<{ message: string; count: number }, { leadIds: string[]; status: string }>({
      query: body => ({
        url: '/leads/bulk-status/',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Lead'],
    }),

    getLeadPipelineSummary: builder.query<PipelineSummary, void>({
      query: () => '/leads/pipeline-summary/',
      transformResponse: (response: { status: string; count: number }[] | PipelineSummary) => {
        if (Array.isArray(response)) {
          const summary: PipelineSummary = {
            NEW: 0,
            CONTACTED: 0,
            REPLIED: 0,
            INTERESTED: 0,
            MEETING: 0,
            WON: 0,
            LOST: 0,
          };
          for (const item of response) {
            if (item.status in summary) {
              summary[item.status as keyof PipelineSummary] = item.count;
            }
          }
          return summary;
        }
        return response;
      },
      providesTags: ['Lead'],
    }),
  }),
});

// Email Template interface
export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  htmlBody?: string;
  category?: 'outreach' | 'follow-up' | 'marketing' | 'onboarding' | 'newsletter';
  variables?: string[];
  createdAt: string;
  updatedAt: string;
}

// Export hooks for usage in functional components
export const {
  // Email Campaigns
  useGetEmailCampaignsQuery,
  useGetEmailCampaignQuery,
  useCreateEmailCampaignMutation,
  useUpdateEmailCampaignMutation,
  useDeleteEmailCampaignMutation,
  useStartEmailCampaignMutation,
  usePauseEmailCampaignMutation,
  useResumeEmailCampaignMutation,
  useCancelEmailCampaignMutation,
  useGetCampaignStatsQuery,
  useGetCampaignLeadsQuery,
  useAddLeadsToCampaignMutation,
  useCloneEmailCampaignMutation,

  // Email Accounts
  useGetEmailAccountsQuery,
  useGetEmailAccountQuery,
  useAddEmailAccountMutation,
  useUpdateEmailAccountMutation,
  useDeleteEmailAccountMutation,
  useTestEmailAccountMutation,
  useInitGmailOAuthMutation,
  useCompleteGmailOAuthMutation,
  useInitOutlookOAuthMutation,
  useCompleteOutlookOAuthMutation,
  useCreateImapSmtpAccountMutation,
  useGetWarmupStatusQuery,
  useStartWarmupMutation,
  usePauseWarmupMutation,
  useUpdateWarmupMaintenanceMutation,
  useAssignAccountToProjectMutation,

  // Email Projects
  useGetEmailProjectsQuery,
  useGetEmailProjectQuery,
  useCreateEmailProjectMutation,
  useUpdateEmailProjectMutation,
  useDeleteEmailProjectMutation,
  useBulkAssignAccountsToProjectMutation,
  useRemoveAccountsFromProjectMutation,

  // Email Templates
  useGetEmailTemplatesQuery,
  useGetEmailTemplateQuery,
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useDuplicateEmailTemplateMutation,

  // Master Inbox
  useGetEmailThreadsQuery,
  useGetEmailThreadQuery,
  useSendReplyMutation,
  useMarkAsReadMutation,

  // Analytics
  useGetAnalyticsOverviewQuery,
  useGetCampaignAnalyticsQuery,
  useGetAnalyticsTimeSeriesQuery,

  // Lead Pipeline
  useUpdateLeadStatusMutation,
  useBulkUpdateLeadStatusMutation,
  useGetLeadPipelineSummaryQuery,
} = emailApi;
